import { Link } from "react-router-dom";
import { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getImportantQuestionsAction } from "../../../../../store/actions/ImportantQuestionAction";
import { Tooltip } from "react-tooltip";
import icon2 from "../../../../../../src/assets/images/icons8-info-80.png";
import PropTypes from "prop-types";

const ImportantQuestion = ({ casePaperData }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [lastPatientId, setLastPatientId] = useState(null);

  const patientDetails = useSelector((state) => state.patients?.patientDetails);
  const questionsDataFromStore = useSelector(
    (state) => state.importantQuestions?.importantQuestions
  );

  // Use case paper data if available, otherwise use store data
  const questionsData =
    casePaperData?.ImportantQuestions || questionsDataFromStore;
  const isCasePaperMode = Boolean(casePaperData);

  const patient_id = patientDetails?.patient_id;

  // Helper function to check if value is base64 data URL
  const isBase64Data = (str) => {
    return str && (str.startsWith('data:application/pdf;base64,') || 
                   str.startsWith('data:image/') ||
                   str.startsWith('data:application/'));
  }

  // Helper function to check if string is a valid URL
  const isValidUrl = (string) => {
    if (!string) return false;
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  // Helper function to get clean filename from base64 or URL
  const getCleanFileName = (url = "") => {
    if (!url) return "";
    if (url.startsWith('http')) {
      const file = url.split("/").pop();
      return file.replace(/^\d+_/, "").substring(0, 50);
    }
    if (url.startsWith('data:application/pdf;base64,')) return 'Document.pdf';
    if (url.startsWith('data:image/png')) return 'Image.png';
    if (url.startsWith('data:image/jpeg')) return 'Image.jpg';
    if (url.startsWith('data:image/jpg')) return 'Image.jpg';
    if (url.startsWith('data:application/')) return 'File.pdf';
    return 'File';
  }

  // Helper function to get file icon
  const getFileIcon = (url) => {
    if (url && (url.includes('.pdf') || url.includes('application/pdf'))) {
      return <i className="fa fa-file-pdf-o me-2" style={{ color: '#dc3545' }}></i>;
    }
    if (url && (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif'))) {
      return <i className="fa fa-file-image-o me-2" style={{ color: '#28a745' }}></i>;
    }
    return <i className="fa fa-file-o me-2"></i>;
  }

  // Function to render answer with file handling
  const renderAnswer = (answer, questionType) => {
    if (!answer) return <span className="text-muted">—</span>;
    
    // Check if it's a file input question with URL or data URL
    if (questionType === "File Input" && (isValidUrl(answer) || isBase64Data(answer))) {
      const isBase64 = isBase64Data(answer);
      const isPdf = isBase64 ? answer.includes('application/pdf') : (answer.toLowerCase().endsWith('.pdf') || answer.includes('application/pdf'));
      const isImage = isBase64 ? answer.includes('image/') : (answer.toLowerCase().endsWith('.png') || answer.toLowerCase().endsWith('.jpg') || answer.toLowerCase().endsWith('.jpeg'));
      const fileName = getCleanFileName(answer);
      
      // For base64 PDF, create a blob URL for better handling
      let displayUrl = answer;
      if (isBase64 && isPdf) {
        try {
          const base64Data = answer.split(',')[1];
          if (base64Data) {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            displayUrl = URL.createObjectURL(blob);
          }
        } catch (e) {
          console.error("Error creating blob URL:", e);
          displayUrl = answer;
        }
      }

      return (
        <div className="mt-1">
          <a 
            href={displayUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-outline-primary btn-sm"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            {getFileIcon(answer)}
            <span>{fileName}</span>
            <i className="fa fa-external-link ms-2"></i>
          </a>
          {isBase64 && (
            <div className="text-muted small mt-1">
              <i className="fa fa-info-circle me-1"></i>
              PDF document (click to view)
            </div>
          )}
        </div>
      );
    }
    
    // For other question types, show regular answer
    return <span className="fs-18 text-black">{answer}</span>;
  };

  useEffect(() => {
    // Only fetch if patient_id exists, is different from last fetch, not already loading, and no case paper data
    if (
      patient_id &&
      patient_id !== lastPatientId &&
      !isLoading &&
      !casePaperData?.ImportantQuestions
    ) {
      setIsLoading(true);
      setLastPatientId(patient_id);

      dispatch(getImportantQuestionsAction(patient_id, "Yes"))
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [
    dispatch,
    patient_id,
    lastPatientId,
    isLoading,
    casePaperData?.ImportantQuestions,
  ]);

  const flattenQuestionAnswers = useCallback((data = []) => {
    const result = [];

    data.forEach((item) => {
      if (item.isGroup === "Yes" && Array.isArray(item.questions)) {
        item.questions.forEach((q) => {
          result.push({
            question_answer_id: q.question_answer_id,
            patient_id: q.patient_id,
            form_id: q.form_id,
            question_id: q.question_id,
            question: q.question || "No question text",
            answer: q.answer || "",
            note_description: q.note_description,
            is_important: q.is_important,
            group_id: q.group_id || item.group_id,
            group_name: item.group_name || "Untitled Group",
            note_add: q.note_add,
            created_at: q.created_at,
            updated_at: q.updated_at,
            isGroup: "No",
            tags: q.tags || [],
            short_form: q.short_form,
            question_type: q.question_type,
          });
        });
      } else {
        result.push({
          ...item,
          question: item.question || "No question text",
          group_name: item.group_name || "",
          isGroup: "No",
          short_form: item.short_form,
          question_type: item.question_type,
        });
      }
    });

    return result;
  }, []);

  const flatQuestions = useMemo(() => {
    // Only process if questionsData is an array and has content
    if (!Array.isArray(questionsData) || questionsData.length === 0) {
      return [];
    }
    return flattenQuestionAnswers(questionsData);
  }, [questionsData, flattenQuestionAnswers]);

  // Filter to only show important questions (is_important === "Yes")
  const importantQuestions = useMemo(() => {
    return flatQuestions.filter(q => q.is_important === "Yes");
  }, [flatQuestions]);

  // Only log when data actually changes and has content
  useEffect(() => {
    if (flatQuestions.length > 0) {
      console.log("Raw data:", questionsData);
      console.log("Flattened questions:", flatQuestions);
      console.log("Important questions:", importantQuestions);
    }
  }, [questionsData, flatQuestions, importantQuestions]);

  if (isLoading) {
    return (
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h4 className="fs-18 text-black font-w600 mb-0">
              Important Questions
            </h4>
          </div>
          <div className="card-body pt-4">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      <div className="card">
        <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
          <h4 className="fs-18 text-black font-w600 mb-0">
            Important Questions
          </h4>
          {!isCasePaperMode && (
            <Link to="/app-questionnaire" className="btn btn-link text-primary">
              Add More
            </Link>
          )}
        </div>
        <div className="card-body pt-4">
        <div
  id="DZ_W_Todo2"
  className={`widget-media dz-scroll ${
    isCasePaperMode
      ? "height370"
      : importantQuestions.length === 0
      ? "height100"
      : importantQuestions.length <= 2
      ? "height180"
      : importantQuestions.length <= 4
      ? "height250"
      : "height370"
  }`}
>
            <ul className="timeline">
              {importantQuestions.length > 0 ? (
                importantQuestions.map((q, idx) => (
                  <li key={`q-${q.question_answer_id || q.question_id || idx}`}>
                    <div className="timeline-panel bgl-dark flex-wrap border-0 p-3 rounded">
                      <div className="media-body">
                        <p className="mb-0 fs-16">
                          {q.short_form?.trim()
                            ? q.short_form
                            : q.label || q.question || "No question"}
                        </p>
                        {/* UPDATED: Use renderAnswer function for file handling */}
                        <div className="mb-1">
                          {renderAnswer(q.answer, q.question_type)}
                        </div>
                        
                        {/* Show note only if it exists */}
                        {q.note_description && q.note_description.trim() !== "" && (
                          <p className="mb-0 fs-14">
                            <strong>Note:</strong> {q.note_description}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-center text-muted">No important questions found.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

ImportantQuestion.propTypes = {
  casePaperData: PropTypes.shape({
    ImportantQuestions: PropTypes.array,
  }),
};

export default ImportantQuestion;